import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { clearAuthHash } from '../authUtils.js';
import { supabase } from '../supabase.js';

interface Props {
  onDone: () => void;
}

/** Shown after the user follows a Supabase password-recovery email link. */
export function ResetPassword({ onDone }: Props) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    if (password !== confirm) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) throw updateErr;
      clearAuthHash();
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <header className="landing-nav">
        <a href="/" className="landing-logo">
          Orio
        </a>
      </header>
      <form className="auth-card" onSubmit={submit}>
        <h2>{t('auth.resetPasswordTitle')}</h2>
        <p className="muted">{t('auth.resetPasswordBody')}</p>
        <label>
          {t('auth.newPassword')}
          <div className="auth-password">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="auth-toggle"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </label>
        <label>
          {t('auth.confirmPassword')}
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
          />
        </label>
        {error && <p className="auth-error">{error}</p>}
        <button type="submit" disabled={busy}>
          {busy ? '…' : t('auth.updatePassword')}
        </button>
      </form>
    </div>
  );
}
